/* eslint-disable @typescript-eslint/no-explicit-any */
import { RegisterPage } from './new/pages/auth/register.page';
import { LoginPage } from './new/pages/auth/login.page';
import { StripeCallbackPage } from './new/pages/influencer/stripe-callback.page';
import {
  type AnyRoute,
  type RouteComponent,
  Outlet,
  createRootRoute,
  createRoute,
  redirect,
} from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { withRouter } from './router/compat';
import auth from './components/auth';
import Application from './Application';
import { NewCollectionPage } from './components/collections/NewCollectionPage';
import ConfigFileEditor from './components/ConfigFileEditor';
import Dashboard from './components/Dashboard';
import {
  AnalyticsOverview,
  PlaybookAdminAnalyticsOverview,
} from './pages/metabase/AnalyticsOverview';
import PortalUserList from './new/pages/admin/portal-users/PortalUserList.page';
import { WorkoutCommentList } from './components/WorkoutCommentList';
import { isLoggedIn } from './new/stores/user.store';
import { OTPLandingPageBuilder } from './pages/CustomProgramPage/OTPLandingPageBuilder';
import { CreatorCrmPage } from './new/pages/crm/CrmUsers.page';
import { InboxPage } from './new/pages/inbox/Inbox.page';
import { LiveStreamPage } from './new/pages/livestream/Livestream.page';
import { NotFoundPage } from './pages/NotFoundPage';
import { ArchivedWorkoutsPage } from './pages/partner/workouts/ArchivedWorkoutsPage';
import { DraftWorkoutsPage } from './pages/partner/workouts/DraftWorkoutsPage';
import { PublishedWorkoutsPage } from './pages/partner/workouts/PublishedWorkoutsPage';
import { ScheduledWorkoutsPage } from './pages/partner/workouts/ScheduledWorkoutsPage';
// import { HomePage } from './new/pages/influencer/home/home.page';
import { ProfilePage } from './new/pages/creator/profile/view/Profile.page';
import { LandingPageBuilder } from './new/pages/LandingPagesBuilder/LandingPageBuilder';
import { CommunityPage } from './new/community/communityPage';
import { PostPage } from './new/community/postPage';
import { CommunityInitPage } from './new/community/communityInitPage';
import { CommunityRouteSwitch } from './new/community/CommunityRouteSwitch';
import {
  OTPListPage,
  SubscriptionListPage,
} from './new/pages/collections/collections.page';
import { ProgramsPage } from './new/pages/programs/Programs.page';
import DomainSetup from './new/pages/creator/settings/domain/DomainSetup/DomainSetup.page';
import { resolveDashboardRedirect } from './new/pages/auth/model/registration.model';
import { CrmWebhooksPage } from './new/pages/crm/CrmWebhooks.page';
import { MarketingPage } from './new/pages/Marketing/Marketing.page';
import { PayoutsPage } from './new/pages/creator/payouts/PayoutsPage';
import { Administration } from './new/pages/admin/Administration.page';
import { InfluencerListNewPage } from './new/pages/admin/influencer-list-new/InfluencerListNew.page';
import { NewCreator } from './new/pages/newCreator/NewCreator.page';
import { SettingsPage } from './new/pages/settings/Settings.page';
import { HomePage } from './new/pages/creator/home/Home.page';
import { CollectionPage } from './new/pages/collection/CollectionPage';
import { MailSubscription } from './new/pages/creator/mail-subscription/MailSubscription.page';
import { PromotionsPage } from './new/pages/promotions/Promotions.page';
import { LibraryPage } from './new/pages/creator/library/views/library.page';
import { PromotionsDetailsPage } from './new/pages/promotions/PromotionDetails.page';
import { ChallengeDetailsPage } from './new/pages/challenges/ChallengeDetails.page';
import { EditWorkoutPage } from './new/pages/workout/WorkoutEdit';
import { ExercisesCatalogPage } from './new/pages/creator/exercises-catalog/ExercisesCatalog.page';
import { ExtrasPage } from './new/pages/creator/extras/Extras.page';
import { BundlesManagementPage } from './new/pages/bundles/BundlesManagement.page';
import { OfferLandingPageBuilder } from './pages/CustomProgramPage/OfferLandingPage';
import { GuidePage } from './new/pages/creator/guide/Guide.page';
import { OnboardingPage } from './new/pages/onboarding/Onboarding.page';
import { CreatorLoginWithPasswordPage } from '@client/new/pages/auth/login/pages/login-with-password.page';
import { rememberPostLoginPath } from './new/pages/auth/model/post-login-path.model';
import { PATHS } from './paths';

export { PATHS, navigate, replacePath, goBack } from './paths';

type Search = Record<string, unknown>;

const toRoutePath = (path: string) =>
  path.replace(/\(\/:(\w+)\)/g, '/{-$$$1}').replace(/:(\w+)/g, '$$$1');

const replaceWith = (to: string, search?: Search, state?: unknown): never => {
  throw redirect({
    to,
    search: search as never,
    state: state as never,
    replace: true,
  });
};

type BeforeLoadArgs = {
  location: { pathname: string; search: Search; href: string };
  cause: 'preload' | 'enter' | 'stay';
};

// react-router 3 ran onEnter once when a route was entered. beforeLoad runs on
// every navigation below the route, so a parent guard skips the "stay" case.
function redirectToLogin({ location, cause }: BeforeLoadArgs) {
  if (cause === 'stay') return;
  if (!isLoggedIn()) {
    rememberPostLoginPath(location.href);
    replaceWith('/login');
  }
}

function redirectToDashboard({ cause }: BeforeLoadArgs) {
  if (cause === 'stay') return;
  const redirectDecision = resolveDashboardRedirect({
    isLoggedIn: isLoggedIn(),
  });

  if (redirectDecision === 'home') {
    replaceWith('/home');
  }
}

const redirectToHomeOrLogin = () =>
  replaceWith(isLoggedIn() ? PATHS.HOME : PATHS.LOGIN);

const keepSearchRedirect =
  (to: string) =>
  ({ location }: BeforeLoadArgs) =>
    replaceWith(to, location.search);

const isAdminLike = () =>
  auth.isAdministrator() || auth.isModerator() || auth.isContentEditor();
const canViewCreatorTools = () =>
  auth.isAdministrator() || auth.isModerator() || auth.isInfluencer();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Page = ComponentType<any>;

const chooseWhen =
  (
    allowed: () => boolean,
    Allowed: Page,
    Fallback: Page = NotFoundPage,
  ): Page =>
  (props) =>
    allowed() ? <Allowed {...props} /> : <Fallback {...props} />;

const withOutlet = (Layout: Page): Page => {
  const Wrapped = withRouter(Layout);
  return () => (
    <Wrapped>
      <Outlet />
    </Wrapped>
  );
};

const page = (
  parent: AnyRoute,
  path: string,
  Component: Page,
  beforeLoad?: (args: BeforeLoadArgs) => void,
) =>
  createRoute({
    getParentRoute: () => parent,
    path: toRoutePath(path),
    component: withRouter(Component) as RouteComponent,
    beforeLoad,
  });

const redirectRoute = (
  parent: AnyRoute,
  path: string,
  beforeLoad: (args: BeforeLoadArgs) => void,
) =>
  createRoute({
    getParentRoute: () => parent,
    path: toRoutePath(path),
    beforeLoad,
  });

const rootRoute = createRootRoute({
  component: withOutlet(Application) as RouteComponent,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'dashboard',
  component: withOutlet(Dashboard) as RouteComponent,
  beforeLoad: redirectToLogin,
});

const guestRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'guest',
  beforeLoad: redirectToDashboard,
});

const guestPages: Array<[string, Page]> = [
  [PATHS.LOGIN, LoginPage],
  [PATHS.LOGIN_CONFIRMATION_CODE, RegisterPage],
  ['/login-with-password', CreatorLoginWithPasswordPage],
  [PATHS.SIGN_UP, RegisterPage],
  [PATHS.SIGN_UP_FILL_NAME, RegisterPage],
  [PATHS.SIGN_UP_INSTAGRAM, RegisterPage],
  [PATHS.SIGN_UP_PHONE, RegisterPage],
  [PATHS.SIGN_UP_CONFIRMATION_CODE, RegisterPage],
];

const guestSignUpAliases = [
  '/signup',
  '/sign-up/referral-source',
  '/sign-up/sales-model',
  PATHS.NEW_SIGN_UP,
  PATHS.NEW_SIGN_UP_DASHED,
];

const adminHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/home/$influencerId',
  component: withRouter(HomePage) as RouteComponent,
  beforeLoad: (args: BeforeLoadArgs) => {
    redirectToLogin(args);
    if (!auth.isAdministrator()) {
      replaceWith('/home', undefined, { nextPathname: args.location.pathname });
    }
  },
});

const dashboardPages: Array<[string, Page]> = [
  ['/admin/home/:influencerId', HomePage],
  ['/collections', PublishedWorkoutsPage],
  ['/stripe-callback', StripeCallbackPage],
  ['/admin', chooseWhen(isAdminLike, InfluencerListNewPage, HomePage)],
  ['/creator/profile', ProfilePage],
  [PATHS.CUSTOM_DOMAIN_SETUP(':influencerId'), DomainSetup],
  ['/admin/workouts', PublishedWorkoutsPage],
  ['/creator/:influencerId/library', LibraryPage],
  ['/admin/partner/:influencerId/edit', ProfilePage],
  [
    '/admin/partner/:influencerId/admin',
    chooseWhen(
      () => auth.isAdministrator() || auth.isModerator(),
      Administration,
      HomePage,
    ),
  ],
  ['/admin/partner/:influencerId/settings', SettingsPage],
  [PATHS.ADMIN_PAYMENT(':influencerId'), PayoutsPage],
  [PATHS.MARKETING(':influencerId'), MarketingPage],
  [PATHS.PROMOTIONS(':influencerId'), PromotionsPage],
  [
    PATHS.PROMOTIONS_DETAILS(':influencerId', ':discountId'),
    PromotionsDetailsPage,
  ],
  [PATHS.ANALYTICS, PlaybookAdminAnalyticsOverview],
  [PATHS.PARTNER_ANALYTICS(':influencerId'), AnalyticsOverview],
  [
    PATHS.CRM_CREATOR(':influencerId', 'users'),
    chooseWhen(canViewCreatorTools, CreatorCrmPage),
  ],
  [
    PATHS.CRM_CREATOR(':influencerId', 'webhooks'),
    chooseWhen(canViewCreatorTools, CrmWebhooksPage),
  ],
  [
    PATHS.CHALLENGE_DETAILS(':influencerId', ':programId', ':challengeId'),
    ChallengeDetailsPage,
  ],
  [PATHS.EXTRAS(':influencerId'), ExtrasPage],
  [PATHS.EXERCISES_CATALOG(':influencerId'), ExercisesCatalogPage],
  [
    PATHS.LANDING_PAGE(':influencerId'),
    chooseWhen(canViewCreatorTools, LandingPageBuilder),
  ],
  [
    '/admin/partner/:influencerId/purchase-page/:programId',
    OTPLandingPageBuilder,
  ],
  [
    '/admin/config-file',
    chooseWhen(
      () => auth.isAdministrator(),
      ConfigFileEditor,
      InfluencerListNewPage,
    ),
  ],
  [PATHS.PARTNER_WORKOUTS(':influencerId'), PublishedWorkoutsPage],
  [PATHS.PARTNER_WORKOUTS_DRAFTS(':influencerId'), DraftWorkoutsPage],
  [PATHS.PARTNER_WORKOUTS_SCHEDULED(':influencerId'), ScheduledWorkoutsPage],
  [PATHS.PARTNER_WORKOUTS_PUBLISHED(':influencerId'), PublishedWorkoutsPage],
  [
    '/admin/partner/:influencerId/workouts/published/:programId',
    PublishedWorkoutsPage,
  ],
  ['/admin/partner/:influencerId/workouts/archived', ArchivedWorkoutsPage],
  ['/admin/:influencerId/workout/:id/comments', WorkoutCommentList],
  ['/admin/partner/:influencerId/workout/:id/edit', EditWorkoutPage],
  ['/admin/partner/:influencerId/class/:id/edit', EditWorkoutPage],
  ['/admin/partner/:influencerId/rest/:id/edit', EditWorkoutPage],
  ['/admin/:influencerId/new-program', NewCollectionPage],
  ['/admin/:influencerId/program/:programId/edit', CollectionPage],
  ['/admin/:influencerId/new-category', NewCollectionPage],
  [PATHS.CATEGORY_EDIT(':programId', ':influencerId'), CollectionPage],
  ['/admin/workout/:id/view', EditWorkoutPage],
  [
    '/admin/portal-users',
    chooseWhen(
      () => auth.isAdministrator(),
      PortalUserList,
      InfluencerListNewPage,
    ),
  ],
  [
    '/admin/new-creator',
    chooseWhen(
      () => auth.isAdministrator() || auth.isModerator(),
      NewCreator,
      InfluencerListNewPage,
    ),
  ],
  ['/admin/livestream', chooseWhen(() => auth.isInfluencer(), LiveStreamPage)],
  [
    '/admin/livestream/upcoming',
    chooseWhen(() => auth.isInfluencer(), LiveStreamPage),
  ],
  [
    '/admin/livestream/past',
    chooseWhen(() => auth.isInfluencer(), LiveStreamPage),
  ],
  ['/inbox', chooseWhen(() => auth.isInfluencer(), InboxPage)],
  [
    PATHS.INBOX_MESSAGE(':influencerId'),
    chooseWhen(() => auth.isAdministrator() || auth.isModerator(), InboxPage),
  ],
  ['/admin/livestream/:influencerId', chooseWhen(isAdminLike, LiveStreamPage)],
  ['/creator/:influencerId/community', CommunityRouteSwitch],
  ['/admin/partner/:influencerId/community', CommunityInitPage],
  ['/creator/:influencerId/community/:id', CommunityPage],
  ['/creator/:influencerId/community/:communityId/post/:postId', PostPage],
  ['/otps(/:influencerId)', OTPListPage],
  ['/programs(/:influencerId)', ProgramsPage],
  ['/subscriptions(/:influencerId)', SubscriptionListPage],
  ['/creator/:influencerId/offers', BundlesManagementPage],
  [
    PATHS.OFFER_LANDING_PAGE_EDITOR(':influencerId', ':offerId'),
    OfferLandingPageBuilder,
  ],
  [PATHS.GUIDE(':influencerId'), GuidePage],
];

const dashboardIndexRoute = createRoute({
  getParentRoute: () => dashboardRoute,
  path: '/home',
  component: withRouter(
    chooseWhen(isAdminLike, InfluencerListNewPage, HomePage),
  ) as RouteComponent,
});

export const routeTree = rootRoute.addChildren([
  redirectRoute(rootRoute, '/', redirectToHomeOrLogin),
  page(rootRoute, '/mail-subscription/:email', MailSubscription),
  page(rootRoute, PATHS.ONBOARDING, OnboardingPage, redirectToLogin),
  page(
    rootRoute,
    '/admin/:influencerId/onboarding',
    OnboardingPage,
    redirectToLogin,
  ),
  guestRoute.addChildren([
    ...guestPages.map(([path, Component]) => page(guestRoute, path, Component)),
    ...guestSignUpAliases.map((path) =>
      redirectRoute(guestRoute, path, keepSearchRedirect(PATHS.SIGN_UP)),
    ),
    redirectRoute(guestRoute, PATHS.NEW_SIGN_IN, () =>
      replaceWith(PATHS.LOGIN),
    ),
  ]),
  adminHomeRoute,
  dashboardRoute.addChildren([
    dashboardIndexRoute,
    ...dashboardPages.map(([path, Component]) =>
      page(dashboardRoute, path, Component),
    ),
  ]),
  redirectRoute(rootRoute, '$', redirectToHomeOrLogin),
]);
